/**
 * Personliga skolrekommendationer — fas 1.
 *
 * Håller frågedefinitioner, sessions-typ, sessionStorage-persistens och
 * en transparent viktad matchningsmodell mot befintlig `education_units`-data.
 *
 * Design: en matchningsorsak visas ENDAST när den kan verifieras mot data
 * som faktiskt finns på skolan (verksamhetstyp, kommun/län, huvudman,
 * fritext-orientation, resource_school). Övriga svar markeras som
 * "okänd information" och sänker `data_coverage_score` — inte `match_score`.
 */
import type { EducationUnit } from "./education-units";

// ---------- Frågedata ----------

export const EDUCATION_TYPES = [
  { value: "Förskola", label: "Förskola" },
  { value: "Grundskola", label: "Grundskola" },
  { value: "Gymnasieskola", label: "Gymnasium" },
  { value: "", label: "Jag är inte säker ännu" },
] as const;

export const TRAVEL_MODES = [
  { value: "walk", label: "Till fots" },
  { value: "bike", label: "Cykel" },
  { value: "car", label: "Bil" },
  { value: "transit", label: "Kollektivtrafik" },
] as const;

export const TRAVEL_LIMITS = [
  { value: "walk", label: "Gångavstånd" },
  { value: "15", label: "Upp till 15 minuter" },
  { value: "30", label: "Upp till 30 minuter" },
  { value: "45", label: "Upp till 45 minuter" },
  { value: "any", label: "Avståndet spelar mindre roll" },
] as const;

export const PERSONALITY_TRAITS = [
  "Social och utåtriktad",
  "Lugn och eftertänksam",
  "Kreativ",
  "Nyfiken och vetgirig",
  "Aktiv och rörelseglad",
  "Tycker om tydliga rutiner",
  "Trivs bäst i mindre grupper",
  "Trivs i större sociala miljöer",
  "Behöver tid för att känna sig trygg",
  "Har starka specialintressen",
];

export const PRIORITIES = [
  "Trygg miljö",
  "Studiero",
  "Engagerade lärare",
  "Små grupper eller klasser",
  "Hög personaltäthet",
  "Tydliga rutiner",
  "Stark elevhälsa",
  "Bra stöd för olika behov",
  "Höga kunskapsresultat",
  "Kreativ undervisning",
  "Mycket rörelse och idrott",
  "Närhet till natur",
  "Bra skolgård eller utemiljö",
  "Social gemenskap",
  "Elevinflytande",
  "Bra fritidshem",
  "Bra skolmat",
  "Modern teknik",
  "Språkprofil",
  "Musik och kultur",
  "Kort resväg",
  "Flexibla öppettider",
  "Tydlig kommunikation med vårdnadshavare",
];

export const PEDAGOGIES = [
  "Traditionell undervisning",
  "Montessori",
  "Waldorf",
  "Reggio Emilia-inspirerad",
  "Utomhuspedagogik eller Ur och Skur",
  "Internationell profil",
  "Språkprofil",
  "Musikprofil",
  "Kulturprofil",
  "Idrottsprofil",
  "Naturvetenskaplig profil",
  "Teknikprofil",
  "Konfessionell inriktning",
  "Anpassad pedagogik",
  "Ingen särskild preferens",
];

/** Nyckelord för fritextmatchning mot `orientation`. */
export const PEDAGOGY_KEYWORDS: Record<string, string[]> = {
  Montessori: ["montessori"],
  Waldorf: ["waldorf"],
  "Reggio Emilia-inspirerad": ["reggio"],
  "Utomhuspedagogik eller Ur och Skur": ["utomhus", "ur och skur", "i ur och skur", "friluft"],
  "Internationell profil": ["internationell", "international", "ib "],
  Språkprofil: ["språk", "engelska", "tvåspråk"],
  Musikprofil: ["musik"],
  Kulturprofil: ["kultur", "estetisk"],
  Idrottsprofil: ["idrott", "sport"],
  "Naturvetenskaplig profil": ["natur", "naturvet"],
  Teknikprofil: ["teknik"],
  "Konfessionell inriktning": ["kristen", "muslim", "judisk", "konfession"],
  "Anpassad pedagogik": ["anpass", "resurs"],
};

// Frivilliga stöd- och anpassningsbehov. Formuleringarna är personcentrerade
// och behovsbaserade — Skolvyn ställer inga diagnoser och avgör inte skolform.
// "Inget särskilt stöd behövs just nu" och "Vill inte ange" ska aldrig tolkas
// som att en elev inte har rätt till stöd vid behov.
export const SUPPORT_NEEDS = [
  "Inget särskilt stöd behövs just nu",
  "Mindre undervisningsgrupper",
  "Tydlig struktur och förutsägbarhet",
  "Lugnare lärmiljö",
  "Extra stöd i undervisningen",
  "Specialpedagogisk kompetens",
  "Tillgång till resursperson",
  "Stöd vid läs- och skrivsvårigheter",
  "Stöd vid matematiksvårigheter",
  "Stöd kring uppmärksamhet, impulsivitet eller aktivitetsnivå (t.ex. vid ADHD)",
  "Stöd kring kommunikation, socialt samspel eller sinnesintryck (t.ex. vid autism)",
  "Stöd vid språk- eller kommunikationssvårigheter",
  "Söker information om anpassad grundskola",
  "Söker information om anpassad gymnasieskola",
  "Svenska som andraspråk",
  "Modersmålsstöd",
  "Fysisk tillgänglighet",
  "Anpassad kost",
  "Stöd vid allergier eller medicinska behov",
  "Stark elevhälsa",
  "Kurator eller skolpsykolog",
  "Vill inte ange",
];

// Alternativ som *inte* ska räknas som ett aktivt stödbehov när vi filtrerar
// eller föreslår skolor.
export const SUPPORT_NEEDS_NEUTRAL = new Set<string>([
  "Inget särskilt stöd behövs just nu",
  "Vill inte ange",
]);

export const ENVIRONMENT_SIZES = [
  "Liten och personlig verksamhet",
  "Mellanstor verksamhet",
  "Stor skola med många möjligheter",
  "Vet inte",
  "Storleken spelar ingen roll",
];

export const SOCIAL_ENVIRONMENT = [
  "Nära relationer med personalen",
  "Många aktiviteter och kompisar",
  "Lugnare miljö",
  "Tydligt arbete mot mobbning",
  "Bra introduktion för nya elever",
  "Vuxennärvaro på raster",
  "Möjlighet att dra sig undan",
  "Stark klassgemenskap",
  "Spelar mindre roll",
];

export const PRACTICAL_WISHES = [
  "Fritidshem",
  "Tidiga öppettider",
  "Sena öppettider",
  "Närhet till kollektivtrafik",
  "Hämtning och lämning med bil",
  "Tillgänglighetsanpassade lokaler",
  "Skolskjuts",
  "Eget kök",
  "Specialkost",
  "Närhet till syskons skola",
  "Öppet hus snart",
  "Lediga platser",
  "Möjlighet att ansöka digitalt",
  "Inget särskilt",
];

export const ORGANIZER_PREFS = [
  { value: "Kommunal", label: "Kommunal" },
  { value: "Enskild", label: "Fristående" },
  { value: "Båda", label: "Båda fungerar" },
  { value: "", label: "Vet inte" },
];

// ---------- Sessions-typ ----------

export type RecommendationSession = {
  educationType: string;
  location: {
    input: string;
    municipalityGuess: string;
  };
  travelLimit: string;
  travelMode: string;
  child: {
    age: string;
    grade: string;
    startYear: string;
    personality: string[];
  };
  priorities: string[];
  topPriorities: string[]; // ordered, up to 3
  pedagogies: string[];
  supportNeeds: string[];
  supportFreeText: string;
  supportFreeTextConsent: boolean;
  environmentSize: string;
  socialEnvironment: string[];
  practical: string[];
  organizer: string;
};

export function emptySession(): RecommendationSession {
  return {
    educationType: "",
    location: { input: "", municipalityGuess: "" },
    travelLimit: "",
    travelMode: "",
    child: { age: "", grade: "", startYear: "", personality: [] },
    priorities: [],
    topPriorities: [],
    pedagogies: [],
    supportNeeds: [],
    supportFreeText: "",
    supportFreeTextConsent: false,
    environmentSize: "",
    socialEnvironment: [],
    practical: [],
    organizer: "",
  };
}

const STORAGE_KEY = "skolvyn:recommendation:v1";

export function loadSession(): RecommendationSession {
  if (typeof window === "undefined") return emptySession();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return emptySession();
    const parsed = JSON.parse(raw);
    return { ...emptySession(), ...parsed };
  } catch {
    return emptySession();
  }
}

export function saveSession(s: RecommendationSession): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* quota etc. — ignore */
  }
}

export function clearSession(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

// ---------- Matchningsmodell ----------

/**
 * Poängmodellen är medvetet transparent: varje kategori har ett tak, och
 * varje skola får en post i `breakdown` med hur mycket den fick och varför.
 * Kategorier som inte går att bedöma mot registerdata räknas inte in i
 * nämnaren — de sänker istället `dataCoverageScore` och listas i `missing`.
 */
const CATEGORY_WEIGHTS = {
  geo: 30,
  type: 15,
  priorities: 20,
  support: 10,
  pedagogy: 10,
  organizer: 10,
  completeness: 5,
} as const;

type CategoryKey = keyof typeof CATEGORY_WEIGHTS;

/** Prioriteringar som går att verifiera mot registerdata i fas 1. */
const PRIORITY_TO_DATA: Record<string, "resource" | "location" | "pedagogy"> = {
  "Bra stöd för olika behov": "resource",
  "Stark elevhälsa": "resource",
  "Kort resväg": "location",
  Språkprofil: "pedagogy",
  "Musik och kultur": "pedagogy",
  "Kreativ undervisning": "pedagogy",
  "Närhet till natur": "pedagogy",
};

export type MatchReason = { label: string; detail?: string };

export type MatchBreakdownItem = {
  key: CategoryKey;
  label: string;
  earned: number;
  max: number;
  /** Kort förklaring i klartext. */
  note: string;
  /** Sant när kategorin inte kunde bedömas mot data. */
  unknown?: boolean;
};

export type MatchResult = {
  unit: EducationUnit;
  matchScore: number;
  dataCoverageScore: number;
  reasons: MatchReason[];
  missing: string[];
  distanceLabel: string;
  breakdown: MatchBreakdownItem[];
  /** En mening som sammanfattar varför skolan föreslås. */
  summary: string;
  tier: "Stark match" | "Bra match" | "Möjlig match" | "Svag match";
};

const CATEGORY_LABELS: Record<CategoryKey, string> = {
  geo: "Läge och närhet",
  type: "Rätt verksamhet",
  priorities: "Era prioriteringar",
  support: "Stöd och anpassningar",
  pedagogy: "Pedagogisk profil",
  organizer: "Huvudman",
  completeness: "Uppgifternas fullständighet",
};

/** Grov geografisk närhetspoäng utan koordinater. */
function geoScore(
  unit: EducationUnit,
  s: RecommendationSession,
): { score: number; label: string; matched: boolean } {
  const q = (s.location.municipalityGuess || s.location.input || "").trim().toLowerCase();
  const muni = (unit.municipality_name || "").toLowerCase();
  const county = (unit.county_name || "").toLowerCase();
  const city = (unit.postal_city || unit.visit_city || "").toLowerCase();
  if (!q) {
    return {
      score: 0.5,
      label: unit.municipality_name ? `I ${unit.municipality_name}` : "Okänt avstånd",
      matched: false,
    };
  }
  const hit = (v: string) => v.length >= 3 && (q.includes(v) || v.includes(q));
  if (muni && hit(muni)) return { score: 1, label: `I ${unit.municipality_name}`, matched: true };
  if (city && hit(city))
    return { score: 0.9, label: `I ${unit.postal_city ?? unit.visit_city}`, matched: true };
  if (county && hit(county))
    return { score: 0.55, label: `I ${unit.county_name}`, matched: true };
  return {
    score: 0.25,
    label: unit.municipality_name
      ? `I ${unit.municipality_name} — utanför ert sökområde`
      : "Utanför ert sökområde",
    matched: false,
  };
}

function matchesEducationType(unit: EducationUnit, chosen: string): boolean {
  if (!chosen) return true;
  const types = unit.education_types ?? [];
  if (chosen === "Grundskola") return types.some((t) => /grundskola|förskoleklass/i.test(t));
  if (chosen === "Förskola") return types.some((t) => /förskola|förskoleklass/i.test(t));
  if (chosen === "Gymnasieskola") return types.some((t) => /gymnasie/i.test(t));
  return types.includes(chosen);
}

function pedagogyMatches(unit: EducationUnit, wanted: string[]): string[] {
  const text = (unit.orientation ?? "").toLowerCase();
  if (!text) return [];
  const hits: string[] = [];
  for (const w of wanted) {
    const kws = PEDAGOGY_KEYWORDS[w];
    if (!kws) continue;
    if (kws.some((k) => text.includes(k))) hits.push(w);
  }
  return hits;
}

export function scoreUnit(unit: EducationUnit, s: RecommendationSession): MatchResult {
  const reasons: MatchReason[] = [];
  const missing: string[] = [];
  const breakdown: MatchBreakdownItem[] = [];

  let earnedSum = 0;
  let scorableSum = 0;
  let relevantSum = 0;

  const add = (
    key: CategoryKey,
    earned: number,
    note: string,
    opts?: { unknown?: boolean; max?: number },
  ) => {
    const max = opts?.max ?? CATEGORY_WEIGHTS[key];
    relevantSum += max;
    if (opts?.unknown) {
      breakdown.push({ key, label: CATEGORY_LABELS[key], earned: 0, max, note, unknown: true });
      return;
    }
    scorableSum += max;
    earnedSum += earned;
    breakdown.push({
      key,
      label: CATEGORY_LABELS[key],
      earned: Math.round(earned),
      max,
      note,
    });
  };

  // --- Läge
  const geo = geoScore(unit, s);
  add("geo", geo.score * CATEGORY_WEIGHTS.geo, geo.label);
  if (geo.matched) reasons.push({ label: geo.label });

  // --- Verksamhetstyp
  const types = unit.education_types ?? [];
  if (s.educationType) {
    const ok = matchesEducationType(unit, s.educationType);
    add(
      "type",
      ok ? CATEGORY_WEIGHTS.type : 0,
      ok
        ? `Erbjuder ${s.educationType.toLowerCase()}`
        : `Ingen ${s.educationType.toLowerCase()} här — skolan har ${types.join(", ") || "okänd verksamhet"}`,
    );
    if (ok) reasons.push({ label: `Erbjuder ${s.educationType.toLowerCase()}`, detail: types.join(", ") });
  } else if (types.length) {
    add("type", CATEGORY_WEIGHTS.type, `Verksamhet: ${types.join(", ")}`);
  }

  // --- Prioriteringar (topp 3 rangordnade)
  const priWeights = [10, 6, 4];
  const top = s.topPriorities.length ? s.topPriorities.slice(0, 3) : s.priorities.slice(0, 3);
  if (top.length > 0) {
    const wantedPedagogy = s.pedagogies.filter((p) => p !== "Ingen särskild preferens");
    top.forEach((p, i) => {
      const w = priWeights[i] ?? 4;
      const kind = PRIORITY_TO_DATA[p];
      if (!kind) {
        missing.push(`${p} — finns ingen jämförbar uppgift i registret ännu`);
        relevantSum += w;
        breakdown.push({
          key: "priorities",
          label: `Prioritet: ${p}`,
          earned: 0,
          max: w,
          note: "Uppgift saknas i registret — fråga skolan direkt",
          unknown: true,
        });
        return;
      }
      let earned = 0;
      let note = "";
      if (kind === "resource" && unit.resource_school) {
        earned = w;
        note = "Registrerad som resursskola";
      } else if (kind === "location" && geo.score >= 0.9) {
        earned = w;
        note = geo.label;
      } else if (kind === "pedagogy" && pedagogyMatches(unit, wantedPedagogy.concat(p)).length) {
        earned = w;
        note = unit.orientation ?? "Profil matchar";
      } else {
        note = "Kunde inte verifieras mot registret";
        missing.push(`${p} — kunde inte verifieras`);
      }
      scorableSum += w;
      relevantSum += w;
      earnedSum += earned;
      breakdown.push({ key: "priorities", label: `Prioritet: ${p}`, earned, max: w, note });
      if (earned > 0) reasons.push({ label: `Matchar prioritet: ${p}`, detail: note });
    });
  }

  // --- Stöd
  const activeSupport = s.supportNeeds.filter((n) => !SUPPORT_NEEDS_NEUTRAL.has(n));
  if (activeSupport.length > 0) {
    const adaptedWanted = activeSupport.some((n) => /anpassad (grundskol|gymnasieskol)/i.test(n));
    const hasAdapted = types.some((t) => /anpassad|specialskola/i.test(t));
    if (adaptedWanted) {
      add(
        "support",
        hasAdapted ? CATEGORY_WEIGHTS.support : 0,
        hasAdapted
          ? `Registrerad för ${types.filter((t) => /anpassad|specialskola/i.test(t)).join(", ")}`
          : "Skolan är inte registrerad som anpassad skola",
      );
      if (hasAdapted) reasons.push({ label: "Registrerad anpassad skola" });
    } else if (unit.resource_school) {
      add("support", CATEGORY_WEIGHTS.support, "Registrerad som resursskola");
      reasons.push({ label: "Registrerad som resursskola" });
    } else {
      add("support", 0, "Skolans stöd och anpassningar finns inte i registret — fråga skolan", {
        unknown: true,
      });
      missing.push("Detaljer om stöd och anpassningar");
    }
  }

  // --- Pedagogik
  const wantsPedagogy = s.pedagogies.filter((p) => p !== "Ingen särskild preferens");
  if (wantsPedagogy.length > 0) {
    const hits = pedagogyMatches(unit, wantsPedagogy);
    if (unit.orientation) {
      add(
        "pedagogy",
        hits.length ? CATEGORY_WEIGHTS.pedagogy : 0,
        hits.length ? `Profil: ${hits.join(", ")}` : `Angiven inriktning: ${unit.orientation}`,
      );
      if (hits.length)
        reasons.push({ label: `Pedagogisk profil: ${hits.join(", ")}`, detail: unit.orientation });
    } else {
      add("pedagogy", 0, "Skolan har ingen registrerad inriktning", { unknown: true });
      missing.push("Uppgift om pedagogisk profil saknas");
    }
  }

  // --- Huvudman
  if (s.organizer === "Kommunal" || s.organizer === "Enskild") {
    const ok = unit.organizer_type === s.organizer;
    const label = s.organizer === "Kommunal" ? "kommunal" : "fristående";
    add(
      "organizer",
      ok ? CATEGORY_WEIGHTS.organizer : 0,
      ok ? `Önskad huvudman: ${label}` : `Huvudman: ${unit.organizer_type ?? "okänd"} (ni önskade ${label})`,
    );
    if (ok) reasons.push({ label: `${s.organizer === "Kommunal" ? "Kommunal" : "Fristående"} huvudman` });
  }

  // --- Fullständighet i registret
  // Bara fält som finns i listvyn (LIST_COLUMNS) — annars blir poängen alltid 0.
  const fields: Array<string | null | undefined> = [
    unit.municipality_name,
    unit.county_name,
    unit.organizer_name,
    unit.orientation,
    types.length ? "types" : "",
  ];
  const filled = fields.filter((f) => (f ?? "").trim().length > 0).length;
  add(
    "completeness",
    (filled / fields.length) * CATEGORY_WEIGHTS.completeness,
    `${filled} av ${fields.length} registeruppgifter finns (kontakt och profil visas på skolsidan)`,
  );


  // --- Miljö och praktiska önskemål: ingen data ännu
  if (s.environmentSize || s.socialEnvironment.length > 0) {
    missing.push("Uppgift om social miljö och skolans storlek saknas");
  }
  if (s.practical.length > 0 && !s.practical.includes("Inget särskilt")) {
    missing.push("Praktiska uppgifter (fritids, öppettider, skolskjuts) saknas");
  }

  const matchScore = scorableSum > 0 ? Math.round((earnedSum / scorableSum) * 100) : 50;
  const dataCoverageScore = relevantSum > 0 ? Math.round((scorableSum / relevantSum) * 100) : 100;

  const tier: MatchResult["tier"] =
    matchScore >= 80 ? "Stark match" : matchScore >= 60 ? "Bra match" : matchScore >= 40 ? "Möjlig match" : "Svag match";

  const strongest = reasons.slice(0, 2).map((r) => r.label.toLowerCase());
  const summary = strongest.length
    ? `${tier}: ${strongest.join(" och ")}.`
    : `${tier}: skolan ligger i ${unit.municipality_name ?? "området"}, men få av era önskemål går att verifiera mot registret.`;

  return {
    unit,
    matchScore,
    dataCoverageScore,
    reasons: reasons.slice(0, 5),
    missing: Array.from(new Set(missing)),
    distanceLabel: geo.label,
    breakdown,
    summary,
    tier,
  };
}

/**
 * Rankar en kandidatlista. Vi filtrerar aldrig bort allt hårt: verksamhetstyp
 * och huvudman påverkar poängen, men skolor som inte matchar hamnar längst ned
 * i stället för att försvinna — annars får användaren en tom sida utan
 * förklaring.
 */
export function rankUnits(units: EducationUnit[], s: RecommendationSession, limit = 24): MatchResult[] {
  const live = units.filter((u) => u.status !== "Nedlagd");
  const scored = live.map((u) => scoreUnit(u, s));
  scored.sort((a, b) => {
    if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore;
    if (b.dataCoverageScore !== a.dataCoverageScore) return b.dataCoverageScore - a.dataCoverageScore;
    return a.unit.name.localeCompare(b.unit.name, "sv");
  });
  return scored.slice(0, limit);
}

